import { useQuery } from '@apollo/client/react';
import {
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { DEFAULT_PAGE_INFO, PriceScopeType } from '@nudool/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TagIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { useDebouncedSearch } from '../../hooks/use-debounced-search.js';
import { usePagedCursor } from '../../hooks/use-paged-cursor.js';
import {
  GET_PRICE_RULE_BRANDS,
  GET_PRICE_RULES_LIST,
  type BrandOption,
  type GetPriceRuleBrandsQueryResult,
  type GetPriceRuleBrandsQueryVariables,
  type GetPriceRulesListQueryResult,
  type GetPriceRulesListQueryVariables,
} from '../../modules/pricing-rules/operations.js';
import { PricingRuleBrandFilter } from '../../modules/pricing-rules/pricing-rule-brand-filter.js';
import { makePricingRuleColumns } from '../../modules/pricing-rules/pricing-rule-columns.js';
import { PricingRuleScopeTypeFilter } from '../../modules/pricing-rules/pricing-rule-scope-type-filter.js';

// Fixed, small enum (packages/shared/src/constants/price-scope-type.ts) -- used the same way
// allBrandIds is: an empty scopeTypeFilter selection means "every scope type", not "none", so an
// empty selection still resolves to this full list rather than an empty `in: []` (which the server
// would read as "match nothing").
const ALL_SCOPE_TYPE_IDS = Object.values(PriceScopeType).filter(
  (value): value is PriceScopeType => typeof value === 'number',
);

const PAGE_SIZE = 20;

export const Route = createFileRoute('/pricing-rules/')({
  component: PricingRulesPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function PricingRulesPage() {
  const navigate = useNavigate();
  const { cursor, hasPreviousPage, goToNextPage, goToPreviousPage, reset } = usePagedCursor();
  const { setSearchInput, search } = useDebouncedSearch(reset);
  // Empty selection means "every accessible brand" -- narrowed by PricingRuleBrandFilter, never
  // a gate the page waits behind (matches routes/product-categories/index.tsx's own pattern).
  const [brandFilter, setBrandFilter] = useState<string[]>([]);
  // Same "empty means every value" convention as brandFilter, narrowed by
  // PricingRuleScopeTypeFilter and resolved server-side via GET_PRICE_RULES_LIST's scopeTypeId
  // where filter (apps/api's PriceRuleWhere already supports it as an IntFilter `in`).
  const [scopeTypeFilter, setScopeTypeFilter] = useState<string[]>([]);
  const effectiveScopeTypeIds = useMemo(
    () => (scopeTypeFilter.length > 0 ? scopeTypeFilter.map(Number) : ALL_SCOPE_TYPE_IDS),
    [scopeTypeFilter],
  );

  const { data: brandData, loading: brandsLoading } = useQuery<
    GetPriceRuleBrandsQueryResult,
    GetPriceRuleBrandsQueryVariables
  >(GET_PRICE_RULE_BRANDS, { variables: { first: 500 } });
  const brands: BrandOption[] = useMemo(
    () => brandData?.priceRuleBrands.edges.map((edge) => edge.node) ?? [],
    [brandData],
  );
  const allBrandIds = useMemo(() => brands.map((brand) => brand.id), [brands]);
  const effectiveBrandIds = brandFilter.length > 0 ? brandFilter : allBrandIds;

  // Same rule as sales-order-columns.tsx / product-tree-columns.tsx's showBrandColumn: a single
  // accessible brand makes the column and the filter pure noise.
  const showBrandColumn = brands.length > 1;
  const columns = useMemo(() => makePricingRuleColumns(showBrandColumn), [showBrandColumn]);

  const { data, loading } = useQuery<GetPriceRulesListQueryResult, GetPriceRulesListQueryVariables>(
    GET_PRICE_RULES_LIST,
    {
      variables: {
        first: PAGE_SIZE,
        after: cursor,
        brandIds: effectiveBrandIds,
        scopeTypeIds: effectiveScopeTypeIds,
        search: search || undefined,
      },
      // Waits for the brand list so the first fetch is already correctly scoped, rather than
      // firing once with an empty brandIds list and again once brands resolve. Matches
      // use-product-tree-nodes.ts's skip: brandIds.length === 0 pattern.
      skip: effectiveBrandIds.length === 0,
    },
  );

  const priceRules = data?.priceRules.edges.map((edge) => edge.node) ?? [];
  const pageInfo = data?.priceRules.pageInfo ?? DEFAULT_PAGE_INFO;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Pricing Rules</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Wholesale and list pricing rules by category, market, product, and account.
        </DimeCardDescription>
      </DimeCardHeader>

      {!brandsLoading && brands.length === 0 ? (
        <ListEmptyState
          icon={<TagIcon />}
          title="No brands available"
          description="You don't have access to any brands yet."
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={priceRules}
          isLoading={loading || brandsLoading}
          loadingRowCount={PAGE_SIZE}
          hasNextPage={pageInfo.hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={() => goToNextPage(pageInfo.endCursor)}
          onPreviousPage={goToPreviousPage}
          showViewOptions
          showExport={false}
          searchColumn="scope"
          searchPlaceholder="Search by category, state, customer, or product…"
          onSearchChange={(value) => setSearchInput(value)}
          filters={
            <>
              {showBrandColumn && (
                <PricingRuleBrandFilter
                  brands={brands}
                  value={brandFilter}
                  onChange={(next) => {
                    setBrandFilter(next);
                    reset();
                  }}
                />
              )}
              <PricingRuleScopeTypeFilter
                value={scopeTypeFilter}
                onChange={(next) => {
                  setScopeTypeFilter(next);
                  reset();
                }}
              />
            </>
          }
          onRowClick={(priceRule) => {
            void navigate({
              to: '/pricing-rules/$priceRuleId',
              params: { priceRuleId: priceRule.id },
            });
          }}
          emptyMessage="No pricing rules found."
        />
      )}
    </DimeCard>
  );
}
